import { useTheme } from "@mui/material/styles";
import { Box, Paper, Typography } from "@mui/material";

import { calculateSummaryStatistics } from "../../../../helper-functions/shared/chartUtils";

/**
 * Status indicator circle with color coding
 * Green = good, Orange = needs improvement
 */
const StatusIndicator = ({ isGood, aim }) => (
    <span>
        <span
            style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: isGood ? "#4caf50" : "#ff9800",
                marginRight: 4,
                verticalAlign: "middle"
            }}
        />
        <span style={{ fontWeight: 500 }}>{isGood ? "good" : "needs improvement"}</span>
        {aim && <span style={{ opacity: 0.7 }}> (aim {aim})</span>}
    </span>
);

/**
 * Get BOP severity color based on percentage
 * Yellow 10-25%, Orange 25-40%, Red 40%+
 */
const getColor = (percentage) => {
    if (percentage >= 40) return "#f72617";
    if (percentage >= 25) return "#fc9700";
    if (percentage >= 10) return "#f0b607";
    return null;
};

/**
 * Format detailed probing sites as "24DP BOP, 36L noBOP"
 */
const formatDetailedSites = (sites, showBOP = true) => {
    if (!sites || sites.length === 0) return null;
    if (!showBOP) return sites.map((s) => `${s.toothNum}${s.site}`).join(", ");
    return sites.map((s) => `${s.toothNum}${s.site} ${s.hasBOP ? "BOP" : "noBOP"}`).join(", ");
};

/**
 * PeriodontalStatistics Component
 * Renders summary statistics for periodontal examination data
 */
export default function PeriodontalStatistics({ teethData, summaryData = null, patientInfo = {} }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    if (!teethData) return null;

    const stats = calculateSummaryStatistics(teethData, summaryData);

    const bopColor = getColor(stats.bleedingPercentage);
    const plaqueColor = getColor(stats.plaquePercentage);
    const pdSitesPct = stats.totalSites > 0 ? Math.round((stats.treatmentSitesCount / stats.totalSites) * 1000) / 10 : 0;
    const pdSitesColor = getColor(pdSitesPct);
    const plaqueIsGood = stats.plaquePercentage < 10;
    const bopIsGood = stats.bleedingPercentage < 10;

    const formatFurcation = () => {
        const { classI, classII, classIII } = stats.teethByFurcation;
        if (classI.length === 0 && classII.length === 0 && classIII.length === 0) return null;
        const parts = [];
        const formatEntries = (entries) => entries.map((e) => `${e.tooth} ${e.site}`).join(", ");
        if (classI.length > 0) parts.push(`${formatEntries(classI)} (Class I)`);
        if (classII.length > 0) parts.push(`${formatEntries(classII)} (Class II)`);
        if (classIII.length > 0) parts.push(`${formatEntries(classIII)} (Class III)`);
        return parts.join("; ");
    };

    const formatMobileTeeth = () => {
        if (stats.mobileTeeth.length === 0) return null;
        const classI = stats.mobileTeeth.filter((t) => t.mobility === 1).map((t) => t.toothNum);
        const classII = stats.mobileTeeth.filter((t) => t.mobility === 2).map((t) => t.toothNum);
        const classIII = stats.mobileTeeth.filter((t) => t.mobility === 3).map((t) => t.toothNum);
        const parts = [];
        if (classI.length > 0) parts.push(`${classI.join(", ")} (Class I)`);
        if (classII.length > 0) parts.push(`${classII.join(", ")} (Class II)`);
        if (classIII.length > 0) parts.push(`${classIII.join(", ")} (Class III)`);
        return parts.join(", ");
    };

    const labelStyle = { fontWeight: 700, mr: 0.5 };
    const lineStyle = { lineHeight: 1.8, color: isDark ? "#ccc" : "#333" };
    const sectionStyle = { lineHeight: 1.8, color: isDark ? "#fff" : "#000", fontWeight: 600 };

    const hasDeep4 = stats.detailedProbingSites.depth4.length > 0;
    const hasDeep5 = stats.detailedProbingSites.depth5.length > 0;
    const hasDeep6 = stats.detailedProbingSites.depth6.length > 0;
    const hasDeep7 = stats.detailedProbingSites.depth7plus.length > 0;
    const hasAnyDeep = hasDeep4 || hasDeep5 || hasDeep6 || hasDeep7;

    const implantText = stats.implantTeeth?.length > 0 ? ` (including implants ${stats.implantTeeth.join(", ")})` : "";
    const teethLine =
        stats.missingCount === 0 && stats.teethCount >= 32
            ? `All ${stats.teethCount} teeth present in mouth${implantText}`
            : `${stats.teethCount} teeth present in mouth${implantText}${
                  stats.missingCount > 0 ? ` with ${stats.missingCount} missing teeth: ${stats.missingTeethList.join(", ")}` : ""
              }`;

    return (
        <Paper
            id="chart-statistics"
            elevation={0}
            sx={{
                width: "100%",
                overflow: "auto",
                maxWidth: "100%",
                margin: "0 auto"
            }}>
            <Box
                sx={{
                    backgroundColor: isDark ? "#2a2a2a" : "#f5f5f5",
                    py: 1.5,
                    px: 2,
                    textAlign: "center"
                }}>
                <Typography variant="h6" fontWeight="bold">
                    Summary Statistics
                </Typography>
                {patientInfo.name && (
                    <Typography variant="body2" color="text.secondary">
                        Patient: {patientInfo.name} | Date: {patientInfo.date || new Date().toLocaleDateString()}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    backgroundColor: isDark ? "#1e3a5f" : "#e3f2fd",
                    border: `1px solid ${isDark ? "#4995e0" : "#1976d2"}`,
                    p: 2,
                    mx: 2,
                    mb: 2,
                    mt: 1,
                    fontSize: "11px"
                }}>
                <Box sx={lineStyle}>
                    <Box component="span" sx={labelStyle}>
                        Teeth:
                    </Box>
                    {teethLine}
                </Box>

                <Box sx={lineStyle}>
                    <Box component="span" sx={labelStyle}>
                        Total sites measured:
                    </Box>
                    {stats.measuredSites} of {stats.availableSites}: 6 point charting per tooth (3 sites buccal, 3 sites palatal/lingual)
                </Box>

                <Box sx={{ ...lineStyle, pt: 1 }}>
                    <Box component="span" sx={labelStyle}>
                        Gingival erythema:
                    </Box>
                    {stats.erythemaRecorded ? (stats.erythemaSiteCodes.length > 0 ? stats.erythemaSiteCodes.join(", ") : "None detected") : "Not recorded"}
                </Box>
                {stats.erythemaRecorded && stats.erythemaSummary && stats.erythemaSiteCodes.length > 0 && <Box sx={lineStyle}>{stats.erythemaSummary}</Box>}

                <Box sx={{ ...lineStyle, pt: 1 }}>
                    <Box component="span" sx={labelStyle}>
                        Plaque:
                    </Box>
                    <Box component="span" sx={plaqueColor ? { fontWeight: 600, color: plaqueColor } : {}}>
                        {stats.plaquePercentage}%
                    </Box>
                    {" sites | "}
                    {stats.plaqueCount}/{stats.totalSites} sites | {stats.plaqueTeethCount}/{stats.teethCount} teeth | <StatusIndicator isGood={plaqueIsGood} aim="<10%" />
                </Box>

                {stats.plaqueSiteCodes?.length > 0 && (
                    <Box sx={lineStyle}>
                        <Box component="span" sx={labelStyle}>
                            Plaque locations:
                        </Box>
                        {stats.plaqueSiteCodes.join(", ")}
                    </Box>
                )}
                {stats.plaqueCount > 0 && stats.plaqueSummary && <Box sx={lineStyle}>{stats.plaqueSummary}</Box>}

                <Box sx={{ ...lineStyle, pt: 1 }}>
                    <Box component="span" sx={labelStyle}>
                        Supragingival calculus:
                    </Box>
                    {stats.calculusRecorded ? (stats.calculusSiteCodes.length > 0 ? stats.calculusSiteCodes.join(", ") : "None detected") : "Not recorded"}
                </Box>
                {stats.calculusRecorded && stats.calculusSummary && stats.calculusSiteCodes.length > 0 && <Box sx={lineStyle}>{stats.calculusSummary}</Box>}

                <Box sx={{ ...lineStyle, pt: 1 }}>
                    <Box component="span" sx={labelStyle}>
                        Bleeding on Probing (BOP):
                    </Box>
                    <Box component="span" sx={bopColor ? { fontWeight: 600, color: bopColor } : {}}>
                        {stats.bleedingPercentage}%
                    </Box>
                    {" sites | "}
                    {stats.bleedingCount}/{stats.totalSites} sites | {stats.bleedingTeethCount}/{stats.teethCount} teeth | <StatusIndicator isGood={bopIsGood} aim="<10%" />
                </Box>

                <Box sx={{ ...sectionStyle, mt: 1 }}>Probing Depths:</Box>
                <Box sx={lineStyle}>Generalised {stats.generalisedRange} probing depths</Box>

                {!hasAnyDeep && <Box sx={{ ...lineStyle, color: isDark ? "#8bc34a" : "#2e7d32" }}>No sites with probing depth ≥4mm</Box>}

                {hasDeep4 && formatDetailedSites(stats.detailedProbingSites.depth4.filter((s) => !s.hasBOP), false) && (
                    <Box sx={lineStyle}>
                        <Box component="span" sx={labelStyle}>
                            Localised 4mm (No BOP) at:
                        </Box>
                        {formatDetailedSites(stats.detailedProbingSites.depth4.filter((s) => !s.hasBOP), false)}
                    </Box>
                )}
                {hasDeep4 && formatDetailedSites(stats.detailedProbingSites.depth4.filter((s) => s.hasBOP), false) && (
                    <Box sx={lineStyle}>
                        <Box component="span" sx={labelStyle}>
                            Localised 4mm with BOP at:
                        </Box>
                        {formatDetailedSites(stats.detailedProbingSites.depth4.filter((s) => s.hasBOP), false)}
                    </Box>
                )}

                {hasDeep5 && (
                    <Box sx={lineStyle}>
                        <Box component="span" sx={labelStyle}>
                            Localised 5mm at:
                        </Box>
                        {formatDetailedSites(stats.detailedProbingSites.depth5)}
                    </Box>
                )}

                {hasDeep6 && (
                    <Box sx={lineStyle}>
                        <Box component="span" sx={labelStyle}>
                            Localised 6mm at:
                        </Box>
                        {formatDetailedSites(stats.detailedProbingSites.depth6)}
                    </Box>
                )}

                {hasDeep7 && (
                    <Box sx={lineStyle}>
                        <Box component="span" sx={labelStyle}>
                            Localised 7mm+ at:
                        </Box>
                        {formatDetailedSites(stats.detailedProbingSites.depth7plus)}
                    </Box>
                )}

                <Box sx={lineStyle}>
                    <Box component="span" sx={labelStyle}>
                        PD sites (≥5mm) + (4mm BOP):
                    </Box>
                    <Box component="span" sx={pdSitesColor ? { fontWeight: 600, color: pdSitesColor } : {}}>
                        {pdSitesPct}%
                    </Box>
                    {" | "}
                    {stats.treatmentSitesCount}/{stats.totalSites} sites | {stats.treatmentTeethCount}/{stats.teethCount} teeth |{" "}
                    <StatusIndicator isGood={stats.treatmentSitesCount === 0} aim="PD ≤4mm" />
                </Box>

                <Box sx={{ ...lineStyle, pt: 1 }}>
                    <Box component="span" sx={labelStyle}>
                        Gingival recession:
                    </Box>
                    {stats.recessionNarrative}
                </Box>

                <Box sx={{ ...lineStyle, pt: 1 }}>
                    <Box component="span" sx={labelStyle}>
                        Furcation:
                    </Box>
                    {formatFurcation() || "No furcation involvement"}
                </Box>

                <Box sx={lineStyle}>
                    <Box component="span" sx={labelStyle}>
                        Mobility:
                    </Box>
                    {formatMobileTeeth() || "No tooth mobility"}
                </Box>
            </Box>
        </Paper>
    );
}
